import { CalendarOff, Plus, Trash2 } from "lucide-react";
import { cn } from "../../shared/ui";
import type { TimesheetDay, WorklogView } from "../../shared/types/api";
import { dayColumnLabel, formatMinutes, isToday } from "./timesheetVocabulary";

/**
 * A week as seven columns (§74).
 *
 * <p>The layout people expect from a timesheet, and the one that makes the week's shape legible:
 * a light Monday next to a heavy Thursday is obvious here and invisible in a list. Entry is by
 * week because that is the rhythm somebody actually fills a timesheet in, whatever §72 cadence
 * the deployment submits on — the same seven columns whether the week is the period itself or
 * one week of a month.
 *
 * <p>Every column carries "logged of expected" rather than just what was logged. A day showing
 * 6h means nothing until you know whether the day was eight hours long, a half day, or a public
 * holiday on which six hours is remarkable.
 */
export function WeekGrid({
  days,
  editable,
  onAdd,
  onEdit,
  onDelete,
}: {
  days: TimesheetDay[];
  editable: boolean;
  onAdd: (date: string) => void;
  onEdit: (entry: WorklogView) => void;
  onDelete: (entry: WorklogView) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[880px] grid-cols-7 gap-px bg-ink-200">
        {days.map((day) => (
          <DayColumn
            key={day.date}
            day={day}
            editable={editable}
            onAdd={() => onAdd(day.date)}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

function DayColumn({
  day,
  editable,
  onAdd,
  onEdit,
  onDelete,
}: {
  day: TimesheetDay;
  editable: boolean;
  onAdd: () => void;
  onEdit: (entry: WorklogView) => void;
  onDelete: (entry: WorklogView) => void;
}) {
  const { weekday, dayOfMonth } = dayColumnLabel(day.date);
  const today = isToday(day.date);
  const nonWorking = !day.workingDay || day.holiday;
  const short = day.expectedMinutes > 0 && day.loggedMinutes < day.expectedMinutes;

  return (
    <div
      className={cn(
        "flex min-h-[280px] flex-col bg-surface",
        nonWorking && "bg-ink-50/60",
        today && "ring-1 ring-inset ring-brand-300",
      )}
    >
      <div className="border-b border-ink-100 px-3 py-2.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex items-baseline gap-1.5">
            <span
              className={cn(
                "text-xs font-medium uppercase tracking-wide",
                today ? "text-brand-700" : "text-ink-500",
              )}
            >
              {weekday}
            </span>
            <span
              className={cn(
                "tnum font-display text-lg font-semibold",
                today ? "text-brand-700" : nonWorking ? "text-ink-500" : "text-ink-900",
              )}
            >
              {dayOfMonth}
            </span>
          </span>

          {day.expectedMinutes > 0 ? (
            <span className="tnum text-xs">
              <span className={short ? "text-amber-700" : "font-medium text-emerald-700"}>
                {formatMinutes(day.loggedMinutes)}
              </span>
              <span className="text-ink-500"> of {formatMinutes(day.expectedMinutes)}</span>
            </span>
          ) : (
            <span className="tnum text-xs text-ink-500">
              {day.loggedMinutes > 0 ? formatMinutes(day.loggedMinutes) : ""}
            </span>
          )}
        </div>

        {/* Why a day expects nothing, when it expects nothing. A blank column with no
            explanation reads as a gap somebody forgot to fill. */}
        {day.holiday && (
          <span className="mt-1 inline-block rounded bg-brand-50 px-1.5 py-0.5 text-[11px] font-medium text-brand-700">
            Public holiday
          </span>
        )}
        {day.onLeave && !day.holiday && (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-ink-600">
            <CalendarOff className="h-3 w-3" />
            On leave
          </span>
        )}
        {!day.workingDay && !day.holiday && !day.onLeave && (
          <span className="mt-1 block text-[11px] text-ink-500">Not a working day</span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-2">
        {day.entries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            editable={editable}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}

        {editable && (
          <button
            type="button"
            onClick={onAdd}
            aria-label={`Log time on ${day.date}`}
            className={cn(
              "flex items-center justify-center gap-1.5 rounded-lg border border-dashed",
              "border-ink-200 py-2 text-xs text-ink-500 transition-colors",
              "hover:border-brand-400 hover:bg-brand-50/50 hover:text-brand-700",
              day.entries.length === 0 && "mt-auto",
            )}
          >
            <Plus className="h-3.5 w-3.5" />
            Log time
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * One entry, as a card.
 *
 * <p>Carries a coloured spine by what the time was against — the eye groups by colour down a
 * column faster than it reads seven project names. Internal time is deliberately a different
 * colour rather than an absent one; it is real time, not a missing project.
 */
function EntryCard({
  entry,
  editable,
  onEdit,
  onDelete,
}: {
  entry: WorklogView;
  editable: boolean;
  onEdit: (entry: WorklogView) => void;
  onDelete: (entry: WorklogView) => void;
}) {
  const internal = !entry.projectId;
  const label = entry.workItemKey ?? entry.projectKey ?? "Internal";
  const title = entry.workItemTitle ?? entry.description ?? entry.projectName ?? "Internal time";

  const content = (
    <>
      <span
        className={cn(
          "absolute inset-y-0 left-0 w-[3px] rounded-l-lg",
          internal ? "bg-ink-400" : "bg-brand-500",
        )}
        aria-hidden
      />
      <span className="block truncate pl-2 text-xs font-medium text-ink-900">{title}</span>
      <span className="mt-0.5 flex items-baseline justify-between gap-2 pl-2">
        <span className="truncate font-mono text-[11px] text-ink-600">{label}</span>
        <span className="tnum shrink-0 text-[11px] font-semibold text-ink-800">
          {formatMinutes(entry.minutes)}
        </span>
      </span>
    </>
  );

  if (!editable || !entry.editable) {
    return (
      <div
        className="relative rounded-lg border border-ink-200 bg-surface px-2 py-1.5"
        title={entry.description ?? undefined}
      >
        {content}
      </div>
    );
  }

  return (
    <div className="group relative">
      <button
        type="button"
        onClick={() => onEdit(entry)}
        title={entry.description ?? "Correct this entry"}
        className="w-full rounded-lg border border-ink-200 bg-surface px-2 py-1.5 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40"
      >
        <span className="relative block">{content}</span>
      </button>

      {/* Shown on hover and on keyboard focus. Seven columns of permanent bin icons is a lot of
          furniture for the rarest action on the screen — but it must stay reachable without a
          mouse, so focus-within brings it back. */}
      <button
        type="button"
        onClick={() => onDelete(entry)}
        aria-label={`Remove ${label}, ${formatMinutes(entry.minutes)}`}
        className={cn(
          "absolute right-1 top-1 rounded-md bg-surface/90 p-1 text-ink-500 opacity-0",
          "transition-opacity hover:bg-red-50 hover:text-red-700",
          "focus-visible:opacity-100 group-hover:opacity-100",
        )}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
}

/** The week's own total, which the seven columns do not add up to on their own. */
export function WeekTotal({ days }: { days: TimesheetDay[] }) {
  const logged = days.reduce((sum, day) => sum + day.loggedMinutes, 0);
  const expected = days.reduce((sum, day) => sum + day.expectedMinutes, 0);
  const share = expected === 0 ? 0 : Math.min(100, (logged / expected) * 100);

  return (
    <div className="flex items-center gap-3">
      <div className="text-right">
        <p className="text-xs text-ink-500">This week</p>
        <p className="tnum text-sm font-semibold text-ink-900">
          {formatMinutes(logged)}
          <span className="font-normal text-ink-500"> of {formatMinutes(expected)}</span>
        </p>
      </div>
      <div className="h-8 w-24 self-center">
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-ink-100">
          <div
            className={cn(
              "h-full rounded-full",
              logged >= expected && expected > 0 ? "bg-emerald-500" : "bg-brand-500",
            )}
            style={{ width: `${share}%` }}
          />
        </div>
      </div>
    </div>
  );
}
